import { get } from 'lodash';
import { retrieveObjectMapper, retrieveDataMapper } from '../..';
import { transformReceipts } from './InvoiceUtils';

export const transformCreditDebitNotes = (...args) => {
  const [
    resp,
    creditDebitNoteItems,
    ecdaRegistrationChildren,
    registrationChildMappings,
    registrationChildren,
    invoices,
    children,
  ] = args;
  const resData = get(resp, 'data.data', []) || [];

  return {
    data: {
      findAllCreditDebitNotes: {
        data: resData.map(item => {
          const regChild =
            ecdaRegistrationChildren?.[item?.fk_ecda_registration_child] ||
            registrationChildren?.[item?.fk_registration_child];
          const fkChild = item?.fk_child || regChild?.fk_child;

          return {
            ...retrieveObjectMapper(item, [
              'ID:id',
              'fk_child',
              'fk_registration_child',
              'fk_ecda_registration',
              'fk_registration',
              'fk_ecda_registration_child',
              'total_amount',
              'pdfUrl:pdf_url_key',
              'pre_signed_url:pdf_url_key_presigned_url',
              'created_at',
              'reasonCode:reason_code_string',
              'remarks',
              'credit_debit_note_no',
              'noteType:note_type_string',
              'type:type',
            ]),
            creditDebitNoteItems: {
              data: creditDebitNoteItems
                .filter(i => i.fk_credit_debit_note === item.id)
                .map(i => ({
                  ...retrieveObjectMapper(i, [
                    'ID:id',
                    'amount',
                    'adjusted_amount',
                    'fk_invoice',
                    'fk_credit_debit_note',
                  ]),
                  invoice: retrieveDataMapper(invoices, i.fk_invoice, [
                    'invoice_no',
                  ]),
                  creditDebitNote: retrieveDataMapper(
                    resData,
                    i.fk_credit_debit_note,
                    ['total_amount', 'credit_debit_note_no']
                  ),
                })),
            },
            child: retrieveDataMapper(children, fkChild, [
              'firstname',
              'lastname',
            ]),
            ecdaRegistrationChild: (() => {
              const ecdaRegistrationChild = get(
                retrieveDataMapper(
                  ecdaRegistrationChildren,
                  item.fk_ecda_registration_child,
                  ['data']
                ),
                'data[0]'
              );
              return ecdaRegistrationChild
                ? retrieveObjectMapper(ecdaRegistrationChild, ['fullname'])
                : null;
            })(),
            registrationChild: (() => {
              const registrationChild = get(
                retrieveDataMapper(
                  registrationChildren,
                  item.fk_registration_child,
                  ['data']
                ),
                'data[0]'
              );
              return registrationChild
                ? {
                    ...retrieveObjectMapper(registrationChild, [
                      'firstname',
                      'lastname',
                    ]),
                    registrationChildMappings: {
                      data: get(
                        retrieveDataMapper(
                          registrationChildMappings,
                          item.fk_registration_child,
                          ['data']
                        ),
                        'data',
                        []
                      )?.map(m => retrieveObjectMapper(m, ['fk_registration'])),
                    },
                  }
                : null;
            })(),
          };
        }),
        totalCount: get(resp, 'data.total_count', 0),
      },
    },
    success: true,
  };
};

export const transformFetchAccountInfoByECDAReg = (...args) => {
  const [
    resp,
    children,
    bankBicCodes,
    bankAccountStateHistories,
    documentTypes,
  ] = args;

  return {
    success: true,
    data: {
      getBankAccountByFkEcdaRegistrationChild: (
        get(resp, 'data.data') || []
      ).map(item => ({
        BankAccount: {
          ...retrieveObjectMapper(item?.bank_account, [
            'ID:id',
            'child_name',
            'child_birth_certificate',
            'payer_account_number',
            'payer_account_name',
            'source:source_string',
            'fk_bank_bic_code',
            'status:status_string',
            'account_type:account_type_string',
            'remarks',
            'bill_reference_number',
            'transaction_number',
            'active',
            'is_cda',
            'is_sibling_cda',
            'sibling_name',
            'sibling_birth_certificate',
            'effective_from',
            'updated_at',
            'created_at',
          ]),
          child: retrieveDataMapper(
            children,
            get(item, 'bank_account.fk_child'),
            ['firstname', 'lastname', 'birth_certificate']
          ),
          bankBicCode: retrieveDataMapper(
            get(bankBicCodes, 'data', []),
            get(item, 'bank_account.fk_bank_bic_code'),
            ['bankName:bank_name', 'ID:id']
          ),
          bankAccountStateHistories: {
            data: get(bankAccountStateHistories, 'data', []).map(h => ({
              updatedAt: get(h, 'updated_at'),
            })),
          },
          documentTypes: {
            data: (() => {
              if (documentTypes[get(item, 'bank_account.id')]?.length > 0)
                return (documentTypes[get(item, 'bank_account.id')] || []).map(
                  doc => ({
                    ID: doc.id,
                    label: doc.label,
                    documentTypeConfig: retrieveObjectMapper(
                      doc.document_type_config,
                      ['ID:id']
                    ),
                    documentTags: {
                      data: (() => {
                        if (doc?.document_tag?.length > 0)
                          return doc.document_tag?.map(tag => ({
                            document: retrieveObjectMapper(tag.document, [
                              'ID:id',
                              'filename',
                              'uRL:url',
                              'preSignedUrl:url_presigned_url',
                            ]),
                          }));
                        return [];
                      })(),
                    },
                  })
                );
              return [];
            })(),
          },
        },
        GiroApplication: item?.giroApplication,
      })),
    },
  };
};

export const transformFetchEgiroDetailsByECDARegChild = (...args) => {
  const [
    resp,
    children,
    bankBicCodes,
    bankAccountStateHistories,
    documentTypes,
  ] = args;

  const bankAccounts = get(resp, 'data.bank_accounts', []);

  return {
    success: true,
    data: {
      getEgiroAccountDetailsByFkEcdaRegistrationChild: bankAccounts.map(
        item => {
          const bankAccount = get(item, 'bank_account', {});
          const giroApplication = get(item, 'giroApplication', null);

          const bankAccountId = get(bankAccount, 'id');
          const fkChild = get(bankAccount, 'fk_child');
          const fkBankBicCode = get(bankAccount, 'fk_bank_bic_code');

          const allHistories = get(bankAccountStateHistories, 'data', []);
          const accountHistories = allHistories.filter(
            h => get(h, 'fk_bank_account') === bankAccountId
          );

          const accountDocs = documentTypes[bankAccountId] || [];

          return {
            BankAccount: {
              ...retrieveObjectMapper(bankAccount, [
                'ID:id',
                'child_name',
                'child_birth_certificate',
                'payer_account_number',
                'payer_account_name',
                'source:source_string',
                'fk_bank_bic_code',
                'status:status_string',
                'account_type:account_type_string',
                'remarks',
                'bill_reference_number',
                'transaction_number',
                'active',
                'is_cda',
                'is_sibling_cda',
                'sibling_name',
                'sibling_birth_certificate',
                'effective_from',
                'updated_at',
                'created_at',
              ]),
              child: retrieveDataMapper(children, fkChild, [
                'firstname',
                'lastname',
                'birth_certificate',
              ]),
              bankBicCode: retrieveDataMapper(
                get(bankBicCodes, 'data', []),
                fkBankBicCode,
                ['bankName:bank_name', 'ID:id']
              ),
              bankAccountStateHistories: {
                data: accountHistories.map(h => ({
                  updatedAt: get(h, 'updated_at'),
                })),
              },
              documentTypes: {
                data: accountDocs.map(doc => ({
                  ID: doc.id,
                  label: doc.label,
                  documentTypeConfig: retrieveObjectMapper(
                    doc.document_type_config,
                    ['ID:id']
                  ),
                  documentTags: {
                    data: (() => {
                      if (doc?.document_tag?.length > 0)
                        return doc.document_tag?.map(tag => ({
                          document: retrieveObjectMapper(tag.document, [
                            'ID:id',
                            'filename',
                            'uRL:url',
                            'preSignedUrl:url_presigned_url',
                          ]),
                        }));
                      return [];
                    })(),
                  },
                })),
              },
            },
            GiroApplication: retrieveObjectMapper(giroApplication, [
              'BankAccountNumber:BankAccountNumber',
              'IsCDA:IsCDA',
              'NameOfAccountHolders:NameOfAccountHolders',
              'MobilePhoneNumber:MobilePhoneNumber',
              'MobilePhoneCountryCode:MobilePhoneCountryCode',
              'ApplicationDate:ApplicationDate',
            ]),
          };
        }
      ),
    },
  };
};

export const transformFetchAccountInfoByRegChild = (...args) => {
  const [
    resp,
    children,
    bankBicCodes,
    bankAccountStateHistories,
    documentTypes,
  ] = args;

  const bankAccounts = get(resp, 'data.bank_accounts', []);

  return {
    success: true,
    data: {
      getEgiroAccountDetailsByFkRegistrationChild: bankAccounts.map(item => {
        const bankAccount = get(item, 'bank_account', {});
        const giroApplication = get(item, 'giroApplication', null);

        const bankAccountId = get(bankAccount, 'id');
        const fkChild = get(bankAccount, 'fk_child');
        const fkBankBicCode = get(bankAccount, 'fk_bank_bic_code');

        const allHistories = get(bankAccountStateHistories, 'data', []);
        const accountHistories = allHistories.filter(
          h => get(h, 'fk_bank_account') === bankAccountId
        );

        const accountDocs = documentTypes[bankAccountId] || [];

        return {
          BankAccount: {
            ...retrieveObjectMapper(bankAccount, [
              'ID:id',
              'child_name',
              'child_birth_certificate',
              'payer_account_number',
              'payer_account_name',
              'source:source_string',
              'fk_bank_bic_code',
              'status:status_string',
              'account_type:account_type_string',
              'remarks',
              'bill_reference_number',
              'transaction_number',
              'active',
              'is_cda',
              'is_sibling_cda',
              'sibling_name',
              'sibling_birth_certificate',
              'effective_from',
              'updated_at',
              'created_at',
            ]),
            child: retrieveDataMapper(children, fkChild, [
              'firstname',
              'lastname',
              'birth_certificate',
            ]),
            bankBicCode: retrieveDataMapper(
              get(bankBicCodes, 'data', []),
              fkBankBicCode,
              ['bankName:bank_name', 'ID:id']
            ),
            bankAccountStateHistories: {
              data: accountHistories.map(h => ({
                updatedAt: get(h, 'updated_at'),
              })),
            },
            documentTypes: {
              data: accountDocs.map(doc => ({
                ID: doc.id,
                label: doc.label,
                documentTypeConfig: retrieveObjectMapper(
                  doc.document_type_config,
                  ['ID:id']
                ),
                documentTags: {
                  data: (() => {
                    if (doc?.document_tag?.length > 0)
                      return doc.document_tag?.map(tag => ({
                        document: retrieveObjectMapper(tag.document, [
                          'ID:id',
                          'filename',
                          'uRL:url',
                          'preSignedUrl:url_presigned_url',
                        ]),
                      }));
                    return [];
                  })(),
                },
              })),
            },
          },
          GiroApplication: retrieveObjectMapper(giroApplication, [
            'BankAccountNumber:BankAccountNumber',
            'IsCDA:IsCDA',
            'NameOfAccountHolders:NameOfAccountHolders',
            'MobilePhoneNumber:MobilePhoneNumber',
            'MobilePhoneCountryCode:MobilePhoneCountryCode',
            'ApplicationDate:ApplicationDate',
          ]),
        };
      }),
    },
  };
};

export const transformAllInvoices = ({
  invoices,
  invoiceItems,
  creditDebitNotes,
  ecdaRegistrationChild,
  registrationChild,
  registrationChildMapping,
  receipts,
  child,
}) =>
  invoices?.map(invoice => {
    const ecdaRegChild =
      ecdaRegistrationChild?.[invoice?.fk_ecda_registration_child];
    const regChild = registrationChild?.[invoice?.fk_registration_child];
    const ecdaRegChildFullname = ecdaRegChild?.fullname;
    const receipt = receipts?.[invoice?.id];
    const currentChild = child?.find(({ ID }) =>
      [ecdaRegChild?.fk_child, regChild?.fk_child, invoice?.fk_child].includes(
        ID
      )
    );

    return {
      ...retrieveObjectMapper(invoice, [
        'ID:id',
        'fk_child',
        'fk_registration',
        'fk_registration_child',
        'fk_ecda_registration',
        'fk_ecda_registration_child',
        'invoice_no',
        'total_tax_amount',
        'total_amount',
        'outstanding_amount',
        'grand_total',
        'pdfUrl:pdf_url_key',
        'pre_signed_url',
        'created_at',
        'remark',
        'label',
        'status:status_string',
        'invoiceType:invoice_type_string',
        'invoice_date',
        'type',
      ]),
      debitNotes: creditDebitNotes[invoice?.id]?.debitNotes || {},
      creditNotes: creditDebitNotes[invoice?.id]?.creditNotes || {},
      invoiceItems: {
        data: invoiceItems?.[invoice?.id] || [],
      },
      child: currentChild || {},
      ecdaRegistrationChild: ecdaRegChildFullname && {
        fullname: ecdaRegChildFullname,
      },
      registrationChild: regChild?.id && {
        ...retrieveObjectMapper(regChild, ['firstname', 'lastname']),
        registrationChildMappings: {
          data: get(
            retrieveDataMapper(
              registrationChildMapping,
              invoice?.fk_registration_child,
              ['data']
            ),
            'data',
            []
          )?.map(rChild => retrieveObjectMapper(rChild, ['fk_registration'])),
        },
      },
      invoiceVoidNotes: {
        // we don't really need this field as there is no data in monolith response as well.
        data: [],
      },
      receipts: {
        data: transformReceipts(receipt),
      },
    };
  });

export const transformGetECDARegistrationRefundChildBalance = (data = []) => {
  const transformedData = data.map(item => {
    return {
      ...retrieveObjectMapper(item, [
        'ID:id',
        'refund_no',
        'refund_at',
        'amount',
        'requested_at',
        'refundMode:refund_mode_string',
        'status:status_string',
        'remark',
        'type',
        'cancellation_reason',
      ]),
    };
  });
  return transformedData;
};

export const transformAdvancePaymentReceipts = (data = []) => {
  const transformAdvancePaymentReceiptsData = data.map(item => {
    return {
      ...retrieveObjectMapper(item, [
        'ID:id',
        'receipt_no',
        'amount',
        'amount_received',
        'credit_amount_used',
        'created_at',
        'paymentType:payment_type_string',
        'pdfUrl:pdf_url_key',
        'preSignedUrl',
        'document_no',
        'cancelation_reason',
        'status',
        'remarks',
        'cancelled_date',
        'unlinked_reason',
        'type',
      ]),
    };
  });
  return transformAdvancePaymentReceiptsData;
};

export const transformAdvancePaymentReceiptsForRegistration = (data = []) => {
  const transformAdvancePaymentReceiptsForRegistrationData = data.map(item => {
    return {
      ...retrieveObjectMapper(item, [
        'ID:id',
        'amount',
        'amount_received',
        'cancelation_reason',
        'cancelled_date',
        'created_at',
        'credit_amount_used',
        'document_no',
        'paymentType:payment_type_string',
        'pdfUrl:pdf_url_key',
        'preSignedUrl',
        'receipt_no',
        'remarks',
        'status',
        'type',
        'unlinked_reason',
      ]),
    };
  });
  return transformAdvancePaymentReceiptsForRegistrationData;
};

export const transformRefundList = (data = []) =>
  data?.map(item =>
    retrieveObjectMapper(item, [
      'ID:id',
      'refund_no',
      'refund_at',
      'amount',
      'requested_at',
      'cancellation_reason',
      'status:status_string',
      'remark',
      'document_no',
      'type',
      'refundMode:refund_mode_string',
    ])
  );

export const transformEcdaRegistrationInvoice = resp => {
  const invoiceData = get(resp, 'data', {});
  const invoiceItems = get(invoiceData, 'invoice_items', {
    data: [],
    totalCount: 0,
  });
  const receipts = get(invoiceData, 'receipts', { data: [], totalCount: 0 });

  return {
    ...retrieveObjectMapper(invoiceData, [
      'ID:id',
      'invoice_no',
      'invoice_type',
      'grand_total',
      'pdf_url',
      'pre_signed_url',
      'total_amount',
      'total_tax_amount',
      'invoice_date',
      'invoice_due_date',
      'total_discount_amount',
      'outstanding_amount',
      'status:status_string',
      'updated_at',
    ]),
    invoiceItems: {
      totalCount: get(invoiceItems, 'totalCount', 0),
      data: (get(invoiceItems, 'data', []) || []).map(item => ({
        ...retrieveObjectMapper(item, [
          'ID',
          'forMonth',
          'fkInvoice',
          'quantity',
          'label',
          'displayAdjustedAmount',
          'unitPrice',
          'taxRate',
          'taxAmount',
          'totalAmount',
        ]),
        billableItem: retrieveObjectMapper(get(item, 'billableItem', {}), [
          'sn1StrAccPacAccountCode',
          'label',
          'unitPrice',
          'taxable',
          'type',
          'cdaDeductible',
          'displayOrder',
        ]),
      })),
    },
    invoiceVoidNotes: {
      totalCount: 0,
      data: [],
    },
    receipts: {
      data: (get(receipts, 'data', []) || []).map(receipt =>
        retrieveObjectMapper(receipt, [
          'ID:id',
          'receipt_no',
          'amount',
          'amount_received',
          'advance_payment_amount',
          'credit_amount_used',
        ])
      ),
      totalCount: get(receipts, 'totalCount', 0),
    },
  };
};

export const transformChildSubsidyHistories = (data = []) =>
  data?.map(dt => ({
    ...retrieveObjectMapper(dt, [
      'invoiceID:invoice_id',
      'invoice_no',
      'invoice_date',
      'billableItemID:billable_item_id',
      'billable_item_label',
      'invoiceItemID:invoice_item_id',
      'final_total_amount',
      'for_month',
      'status',
      'add_by',
    ]),
    listCreditDebitNote: {
      data:
        dt?.list_credit_debit_note?.data?.map(note => ({
          ...retrieveObjectMapper(note, [
            'created_at',
            'credit_debit_note_no',
            'credit_debit_note_date',
            'remarks',
          ]),
          userByGeneratedByFkStaff: note?.userByGeneratedByFkStaff && {
            ...retrieveObjectMapper(note?.userByGeneratedByFkStaff, [
              'firstname',
              'lastname',
            ]),
          },
        })) || [],
    },
  })) || [];

export const transformDiscountGroups = (
  discountGroups = [],
  centreRelations = {}
) =>
  discountGroups?.map(item => ({
    ...retrieveObjectMapper(item, [
      'ID:id',
      'fkSchool:fk_school',
      'name',
      'discountItemType:discount_item_type_string',
      'effectiveDate:effective_date',
      'ceasedDate:ceased_date',
      'createdAt:created_at',
      'taxable',
      'amount',
      'status:status_string',
      'applicationLevel:application_level_string',
      'isRecurrent:is_recurrent',
      'accPacCode:acc_pac_code',
      'hasDiscountArrangement:has_discount_arrangement',
      'applyTo:apply_to_string',
    ]),
    discountItemGroupCentreRelations: {
      data: (centreRelations[item.id] || []).map(relation => ({
        centre: {
          label: get(relation, 'label'),
        },
      })),
    },
  })) || [];

export const transformFindAllValidDownloadedGiroFiles = (resp, users = []) => {
  const data = get(resp, 'data.data', []);
  const totalCount = get(resp, 'data.total_count') || data.length;

  return {
    data: {
      findAllValidDownloadedGiroFiles: {
        totalCount,
        data: data.map(d => {
          const baseFields = retrieveObjectMapper(d, [
            'ID:id',
            'label',
            'uRL:url',
            'status:status_string',
            'log',
            'docType:doc_type_string',
            'presignedUrl:pre_signed_url',
            'createdAt:created_at',
            'uploadedAt:uploaded_at',
          ]);

          const userObj = retrieveDataMapper(users, d.fk_user, [
            'ID:id',
            'firstname',
            'lastname',
          ]);

          return {
            ...baseFields,
            giroStatus: get(d, 'giro_status') || null,
            fkFinanceDocument: get(d, 'fk_finance_document') || null,
            user: userObj,
            financeDocuments: {
              data: [
                {
                  ...baseFields,
                  errorLog: get(d, 'error_log') || null,
                  user: userObj,
                },
              ],
            },
          };
        }),
      },
    },
    success: true,
  };
};

export default {
  transformCreditDebitNotes,
  transformFetchAccountInfoByECDAReg,
  transformFetchAccountInfoByRegChild,
  transformGetECDARegistrationRefundChildBalance,
  transformAdvancePaymentReceipts,
  transformAdvancePaymentReceiptsForRegistration,
  transformRefundList,
  transformEcdaRegistrationInvoice,
  transformChildSubsidyHistories,
  transformDiscountGroups,
  transformFindAllValidDownloadedGiroFiles,
};
